import {
  Datagrid,
  FunctionField,
  List,
  NumberInput,
  SelectInput,
  TextInput,
} from 'react-admin';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';

import { displayNameFor, formatDate, semanticStateColor } from '@src/components/indexPage';

import { formatMoney } from './format';
import OrderExpand from './OrderExpand';
import OrderListError from './OrderListError';
import { useOrderConstants } from './useOrderConstants';
import { present } from './types';
import type { ConstantOption, OrderRecord } from './types';

const orderNumber = (order: OrderRecord): string => order.uuid.split('-')[0].toUpperCase();

const activeItemCount = (order: OrderRecord): number =>
  present(order.orderItems).filter(item => !item.removedAt).length;

const toChoices = (options: ConstantOption[]) =>
  options.map(({ code, displayName }) => ({ id: code, name: displayName }));

const YES_NO = [{ id: 'true', name: 'Yes' }, { id: 'false', name: 'No' }];
const PLATFORMS = [{ id: 'shopify', name: 'Shopify' }, { id: 'legacy', name: 'Trove' }];

const OrderList = () => {
  const {
    states,
    ypackageStates,
    conditions,
    refundReasons,
    giftcardReasons,
    returnReasons,
    freeShippingReasons,
    isFallback,
  } = useOrderConstants();

  const filters = [
    // A TextInput, not a SearchInput: the resolver maps `orderNumber` to `uuid_prefix` by
    // equality (uppercased), so the magnifying-glass affordance promised a substring search that
    // does not exist. The order number shown in the list is that same 8-character uuid prefix,
    // so a partial one — or a customer name — matches nothing.
    <TextInput
      key="orderNumber"
      source="orderNumber"
      label="Order number"
      placeholder="Full order number, exact match"
      alwaysOn
    />,
    <SelectInput key="state" source="state" label="Status" choices={toChoices(states)} />,
    <SelectInput key="ypackageState" source="ypackageState" label="Package status" choices={toChoices(ypackageStates)} />,
    <TextInput key="email" source="email" label="Email" />,
    <TextInput key="lastName" source="lastName" label="Last name" />,
    <TextInput key="firstName" source="firstName" label="First name" />,
    <NumberInput key="orderId" source="orderId" label="Order ID" />,
    <TextInput key="orderUuid" source="orderUuid" label="Order UUID" />,
    <TextInput key="externalOrderNumber" source="externalOrderNumber" label="External order #" />,
    <SelectInput key="commercePlatform" source="commercePlatform" label="Platform" choices={PLATFORMS} />,
    <SelectInput key="captured" source="captured" label="Payment captured" choices={YES_NO} />,
    <SelectInput key="hasReturns" source="hasReturns" label="Has returns" choices={YES_NO} />,
    <SelectInput key="hasEscalations" source="hasEscalations" label="Has escalations" choices={YES_NO} />,
  ];

  return (
    <>
      {isFallback && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Showing default filter options — live order and package states, conditions, and reasons
          couldn&apos;t be loaded, so filtering may be incomplete.
        </Alert>
      )}
      {/* Without an `error` node react-admin renders the children anyway, so a failed query
          shows an empty datagrid behind a toast — indistinguishable from a partner with no
          orders. */}
      <List
        resource="orders"
        title="Orders"
        filters={filters}
        sort={{ field: 'orderPlacedAt', order: 'DESC' }}
        perPage={25}
        exporter={false}
        error={<OrderListError />}
      >
        <Datagrid
          bulkActionButtons={false}
          expand={(
            <OrderExpand
              states={states}
              conditions={conditions}
              refundReasons={refundReasons}
              giftcardReasons={giftcardReasons}
              returnReasons={returnReasons}
              freeShippingReasons={freeShippingReasons}
            />
          )}
          rowClick="expand"
          sx={{ '& .RaDatagrid-rowCell': { verticalAlign: 'middle' } }}
        >
          <FunctionField<OrderRecord>
            source="uuid"
            label="Order"
            sortable={false}
            render={order => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "'SFMono-Regular', Consolas, monospace" }}>
                  {orderNumber(order)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {`#${order.id}${order.externalOrderNumber ? ` · ${order.externalOrderNumber}` : ''}`}
                </Typography>
              </Box>
            )}
          />
          <FunctionField<OrderRecord>
            label="Customer"
            sortable={false}
            render={order => (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  {order.deliveryAddress?.name ?? '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {order.deliveryAddress?.email ?? ''}
                </Typography>
              </Box>
            )}
          />
          <FunctionField<OrderRecord>
            label="Status"
            sortable={false}
            render={order => (
              <Chip size="small" color={semanticStateColor(order.state)} label={displayNameFor(states, order.state)} />
            )}
          />
          <FunctionField<OrderRecord>
            label="Items"
            sortable={false}
            textAlign="right"
            render={order => activeItemCount(order)}
          />
          <FunctionField<OrderRecord>
            label="Total"
            sortable={false}
            textAlign="right"
            render={order => (
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(order.total, order.transactionCurrencyCode)}
              </Typography>
            )}
          />
          <FunctionField<OrderRecord>
            label="Placed"
            sortable
            source="orderPlacedAt"
            render={order => (
              <Stack>
                <Typography variant="body2">{formatDate(order.orderPlacedAt)}</Typography>
                {!order.orderCapturedAt && order.state !== 'CANCELED' && (
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>Uncaptured</Typography>
                )}
              </Stack>
            )}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default OrderList;
